import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import HowToPlay from "@/components/HowToPlay";
import QuestionExtras from "@/components/QuestionExtras";
import { Sword, CheckCircle, XCircle, ArrowRight, Hourglass, ClockCounterClockwise } from "@phosphor-icons/react";

const API_ROOT = process.env.REACT_APP_BACKEND_URL || "";

const GATE_SECONDS = 30;

function notifyResolved(match, user) {
  const isA = match.player_a === user?.user_id;
  const myScore = isA ? match.score_a : match.score_b;
  const oppScore = isA ? match.score_b : match.score_a;
  const oppName = isA ? match.player_b_name : match.player_a_name;
  const youWin = match.winner === user?.user_id;
  const isTie = !match.winner;
  const verb = youWin ? "won" : isTie ? "tied" : "lost";
  const title = youWin ? "You won the duel" : isTie ? "Duel tied" : "Duel lost";
  const body = `vs ${oppName || "opponent"} · ${myScore}–${oppScore}`;
  // In-app toast
  toast[youWin ? "success" : isTie ? "message" : "error"](title, { description: body });
  // Browser notification (best-effort)
  if (typeof window !== "undefined" && "Notification" in window
      && Notification.permission === "granted" && document.hidden) {
    try {
      const n = new Notification(`NKognito · ${title}`, {
        body, tag: `duel-${match.match_id}`, silent: false,
      });
      n.onclick = () => { window.focus(); n.close(); };
    } catch { /* ignore */ }
  }
}

export default function Duel() {
  const { user } = useAuth();
  const [matches, setMatches] = useState([]);
  const [queueId, setQueueId] = useState(null);
  const [queueState, setQueueState] = useState(null); // GATING | WAITING | MATCHED
  const [gateQ, setGateQ] = useState(null);
  const [gateStartedAt, setGateStartedAt] = useState(null);
  const [gateSecLeft, setGateSecLeft] = useState(GATE_SECONDS);
  const [gatePick, setGatePick] = useState(null);
  const [gateResult, setGateResult] = useState(null); // 'correct' | 'wrong'

  const [active, setActive] = useState(null); // active duel match
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [tStart, setTStart] = useState(0);
  const [questionStartedAt, setQuestionStartedAt] = useState(0); // when current Q displayed
  const [now, setNow] = useState(Date.now());
  const [localPick, setLocalPick] = useState(null); // buffered pick per duel question
  const [submitting, setSubmitting] = useState(false);
  const QUESTION_SECONDS = 15;
  const localPickRef = useRef(null);
  useEffect(() => { localPickRef.current = localPick; }, [localPick]);

  const pollRef = useRef(null);

  const seenResolvedRef = useRef(new Set());
  const matchesLoadedRef = useRef(false);
  const notifPermAskedRef = useRef(false);

  const loadMatches = async () => {
    try {
      const { data } = await api.get("/duel/my-matches");
      setMatches(data);
      // Notify on freshly-resolved matches (skip the first load — those are historical)
      if (matchesLoadedRef.current) {
        for (const m of data) {
          if (m.status === "RESOLVED" && !seenResolvedRef.current.has(m.match_id)) {
            seenResolvedRef.current.add(m.match_id);
            notifyResolved(m, user);
          }
        }
      } else {
        // First load — mark all currently resolved as seen
        for (const m of data) {
          if (m.status === "RESOLVED") seenResolvedRef.current.add(m.match_id);
        }
        matchesLoadedRef.current = true;
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadMatches();
    // Request browser-notification permission once per mount
    if (!notifPermAskedRef.current && typeof window !== "undefined" && "Notification" in window
        && Notification.permission === "default") {
      notifPermAskedRef.current = true;
      try { Notification.requestPermission(); } catch { /* ignore */ }
    }
    const id = setInterval(loadMatches, 8000);
    return () => clearInterval(id);
  }, []);

  // Gate question timer
  useEffect(() => {
    if (!gateQ || !gateStartedAt || gateResult) return;
    const start = new Date(gateStartedAt).getTime();
    const tick = () => {
      const elapsed = (Date.now() - start) / 1000;
      const left = Math.max(0, GATE_SECONDS - elapsed);
      setGateSecLeft(left);
      if (left <= 0 && !gatePick) {
        // Auto-expire
        setGateResult("wrong");
        toast.error("Gate timed out");
      }
    };
    tick();
    const id = setInterval(tick, 100);
    return () => clearInterval(id);
  }, [gateQ, gateStartedAt, gateResult, gatePick]);

  // Poll for match once WAITING
  useEffect(() => {
    if (queueState !== "WAITING" || !queueId) return;
    const poll = async () => {
      try {
        const { data } = await api.get(`/duel/poll/${queueId}`);
        if (data.match_id) {
          setQueueState("MATCHED");
          const { data: m } = await api.get(`/duel/match/${data.match_id}`);
          setActive(m);
          setStep(0);
          setAnswers([]);
          setTStart(performance.now());
          setQuestionStartedAt(Date.now());
          toast.success("Opponent found — go!");
          loadMatches();
        }
      } catch { /* ignore */ }
    };
    poll();
    pollRef.current = setInterval(poll, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [queueState, queueId]);

  const enterQueue = async () => {
    try {
      const { data } = await api.post("/duel/enter", {});
      setQueueId(data.queue_id);
      setQueueState(data.state);
      // Gates are removed — backend now returns WAITING directly. Keep the old
      // GATING branch behind a guard for safety, but it should never fire.
      if (data.state === "GATING") {
        // Legacy path; auto-skip by treating as WAITING.
        setQueueState("WAITING");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't enter matchmaking");
    }
  };

  const submitGate = async (i) => {
    if (gatePick !== null || gateResult) return;
    setGatePick(i);
    try {
      const { data } = await api.post("/duel/gate-answer", { queue_id: queueId, option_index: i });
      setGateResult(data.correct ? "correct" : "wrong");
      setQueueState("WAITING");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't submit");
      setGatePick(null);
    }
  };

  const cancelQueue = async () => {
    if (!queueId) return;
    try { await api.post(`/duel/cancel/${queueId}`); } catch { /* ignore */ }
    resetQueue();
  };

  const resetQueue = () => {
    setQueueId(null); setQueueState(null);
    setGateQ(null); setGateStartedAt(null); setGatePick(null); setGateResult(null);
  };

  const togglePick = (i) => {
    if (submitting) return;
    setLocalPick((cur) => (cur === i ? null : i));
  };

  const pickAnswer = async (idx) => {
    if (submitting) return;
    setSubmitting(true);
    const next = [...answers, idx];
    setAnswers(next);
    setLocalPick(null);
    if (next.length < active.question_details.length) {
      setStep(step + 1);
      setQuestionStartedAt(Date.now());
      setSubmitting(false);
    } else {
      try {
        const total = Math.round(performance.now() - tStart);
        await api.post("/duel/submit", { match_id: active.match_id, answers: next, total_time_ms: total });
        toast.success("Round submitted — waiting on opponent if not done.");
        setActive(null);
        resetQueue();
        loadMatches();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Submit failed");
      } finally {
        setSubmitting(false);
      }
    }
  };

  // Per-question 15-second timer for active duel match
  useEffect(() => {
    if (!active || !active.question_details) return;
    const id = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(id);
  }, [active, step]);

  // Auto-submit buffered pick when timer hits 0 (or -1 if no pick)
  useEffect(() => {
    if (!active || !active.question_details || !questionStartedAt) return;
    const elapsedMs = Date.now() - questionStartedAt;
    if (elapsedMs >= QUESTION_SECONDS * 1000 + 200) {
      pickAnswer(localPickRef.current ?? -1);
    }
  }, [now, active, step, questionStartedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const resume = async (match_id) => {
    try {
      const { data } = await api.get(`/duel/match/${match_id}`);
      setActive(data);
      setStep(0);
      setAnswers([]);
      setLocalPick(null);
      setTStart(performance.now());
      setQuestionStartedAt(Date.now());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load match");
    }
  };

  // ---------- Render ----------

  // Active match — playing your 5 questions
  if (active && active.question_details) {
    const q = active.question_details[step];
    const elapsedSec = (now - questionStartedAt) / 1000;
    const secLeft = Math.max(0, QUESTION_SECONDS - elapsedSec);
    const pct = (secLeft / QUESTION_SECONDS) * 100;
    const urgency = secLeft < 3 ? "urgent" : secLeft < 5 ? "warn" : "normal";
    return (
      <div className="max-w-2xl mx-auto space-y-5">
        <header className="flex items-center justify-between gap-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-medium truncate">NK Duel · {active.match_id.slice(-6)}</p>
          <span className="text-xs text-white/55 font-mono shrink-0">Q {step + 1} / {active.question_details.length}</span>
        </header>
        <div className="space-y-1.5" data-testid="duel-timer">
          <div className="flex items-center justify-between text-[11px] font-mono">
            <span className="text-white/55">Time</span>
            <span className={`tabular-nums font-display ${
              urgency === "urgent" ? "text-nk-bad" : urgency === "warn" ? "text-nk-warn" : "text-nk-gold"
            }`}>
              {Math.ceil(secLeft)}s
            </span>
          </div>
          <div className="h-1.5 nk-timer-bar rounded-full overflow-hidden">
            <div
              className={`h-full nk-timer-fill ${urgency === "urgent" ? "nk-timer-urgent" : urgency === "warn" ? "nk-timer-warn" : ""}`}
              style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
            />
          </div>
        </div>
        <div className="nk-board rounded-md p-5 sm:p-7">
          <p className="text-[10px] uppercase tracking-[0.16em] text-nk-gold/70 font-mono mb-4">{q.category}</p>
          <p className="font-display text-xl sm:text-2xl font-medium leading-snug mb-6 text-white">{q.body}</p>
          <QuestionExtras extras={q.nke_extras} qid={q.qid} apiUrl={API_ROOT} />
          <div className="grid gap-3">
            {q.options.map((opt, i) => {
              const isSelected = localPick === i;
              return (
                <button
                  key={i}
                  data-testid={`duel-option-btn-${i}`}
                  onClick={() => togglePick(i)}
                  disabled={submitting}
                  aria-pressed={isSelected}
                  className={`nk-option text-left rounded-md px-4 py-4 flex items-center gap-3 ${isSelected ? "nk-option-selected" : ""}`}
                >
                  <span className={`font-mono text-xs w-5 ${isSelected ? "text-nk-gold" : "text-white/45"}`}>
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span className="flex-1 text-sm sm:text-base">{opt}</span>
                  {isSelected && <CheckCircle size={18} weight="fill" className="text-nk-gold shrink-0" />}
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-white/55" data-testid="duel-pick-hint">
              {localPick === null
                ? "Pick an answer. You can change it until time runs out."
                : <>You picked <span className="font-mono text-nk-gold">{String.fromCharCode(65 + localPick)}</span>. Tap to change, or lock in.</>}
            </p>
            <button
              type="button"
              data-testid="duel-lockin-btn"
              onClick={() => pickAnswer(localPick)}
              disabled={localPick === null || submitting}
              className="nk-btn nk-btn-gold disabled:opacity-40 disabled:cursor-not-allowed text-xs px-4 py-2"
            >
              Lock in <ArrowRight size={12} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Gate question UI
  if (gateQ && !gateResult) {
    const pct = (gateSecLeft / GATE_SECONDS) * 100;
    const urgency = gateSecLeft < 3 ? "urgent" : gateSecLeft < 5 ? "warn" : "normal";
    return (
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/55 font-medium truncate">Gate · qualify for matchmaking</p>
          <span className={`text-sm font-mono shrink-0 tabular-nums font-display ${
            urgency === "urgent" ? "text-nk-bad" : urgency === "warn" ? "text-nk-warn" : "text-nk-gold"
          }`}>
            {Math.ceil(gateSecLeft)}s
          </span>
        </div>
        <div className="h-1.5 nk-timer-bar rounded-full overflow-hidden">
          <div
            className={`h-full nk-timer-fill ${urgency === "urgent" ? "nk-timer-urgent" : urgency === "warn" ? "nk-timer-warn" : ""}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="nk-board rounded-md p-5 sm:p-7">
          <p className="text-[10px] uppercase tracking-[0.16em] text-nk-gold/70 font-mono mb-4">{gateQ.category}</p>
          <p className="font-display text-xl sm:text-2xl font-medium leading-snug mb-6 text-white">{gateQ.body}</p>
          <div className="grid gap-3">
            {gateQ.options.map((opt, i) => {
              const isSelected = gatePick === i;
              return (
                <button
                  key={i}
                  data-testid={`duel-gate-option-${i}`}
                  onClick={() => submitGate(i)}
                  disabled={gatePick !== null}
                  className={`nk-option text-left rounded-md px-4 py-4 flex items-center gap-3 ${isSelected ? "nk-option-selected" : ""}`}
                >
                  <span className={`font-mono text-xs w-5 ${isSelected ? "text-nk-gold" : "text-white/45"}`}>{String.fromCharCode(65 + i)}</span>
                  <span className="flex-1 text-sm sm:text-base">{opt}</span>
                  {isSelected && <CheckCircle size={18} weight="fill" className="text-nk-gold shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
        <button
          onClick={cancelQueue}
          className="text-xs text-white/55 hover:text-white transition-colors"
        >Cancel matchmaking</button>
      </div>
    );
  }

  // Waiting for opponent
  if (queueState === "WAITING") {
    return (
      <div className="max-w-md mx-auto text-center space-y-6 py-10">
        <Hourglass size={32} weight="duotone" className="text-nk-accent mx-auto animate-pulse-dot" />
        <p className="eyebrow">Matchmaking</p>
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">
          Searching for an opponent…
        </h1>
        <p className="text-sm text-nk-mute">
          We will pair you with a player near your rating. If no one joins in 60 seconds, you will play alone.
        </p>
        <button
          onClick={cancelQueue}
          className="text-xs text-nk-mute hover:text-nk-bad transition-colors"
        >Cancel</button>
      </div>
    );
  }

  // Default landing — Find a match
  return (
    <div className="space-y-7 max-w-3xl">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-nk-gold/80 font-medium mb-2">NK Duel</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white">
            5 questions. <span className="italic font-light text-nk-gold">One opponent.</span>
          </h1>
          <p className="text-sm sm:text-base text-white/65 mt-3 max-w-md leading-relaxed">
            Win <span className="text-nk-gold font-mono">+20 SS</span>. Lose, still keep <span className="font-mono">+5 SS</span>.
          </p>
        </div>
        <button
          data-testid="duel-find-match-btn"
          onClick={enterQueue}
          className="nk-btn nk-btn-gold px-6 py-3.5 shrink-0"
        >
          <Sword size={16} weight="bold" />
          Find a match
        </button>
      </header>

      <section>
        {(() => {
          const pending = matches.filter((m) => m.status !== "RESOLVED");
          const recent = matches.filter((m) => m.status === "RESOLVED").slice(0, 5);

          const renderPending = (m) => {
            const isA = m.player_a === user?.user_id;
            const myPlayed = isA ? m.played_a : m.played_b;
            const oppName = (isA ? m.player_b_name : m.player_a_name) || "Player";
            return (
              <div key={m.match_id} className="nk-card p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm truncate text-white">
                    <span className="font-medium">You</span>
                    <span className="text-white/55"> vs </span>
                    <span className="font-medium">{oppName}</span>
                  </p>
                  <p className="text-[11px] text-white/55 mt-1">
                    {myPlayed ? "Waiting for your opponent to play" : "Your turn — answer your 5 questions"}
                  </p>
                </div>
                {myPlayed ? (
                  <span className="text-[11px] text-nk-warn shrink-0">Waiting…</span>
                ) : (
                  <button
                    onClick={() => resume(m.match_id)}
                    className="nk-btn nk-btn-gold px-3 py-1.5 text-xs shrink-0"
                  >Play <ArrowRight size={12} weight="bold" /></button>
                )}
              </div>
            );
          };

          const renderResult = (m) => {
            const isA = m.player_a === user?.user_id;
            const solo = m.is_solo || m.player_b === "bot_solo";
            const myScore = (isA ? m.score_a : m.score_b) ?? 0;
            const oppScore = (isA ? m.score_b : m.score_a) ?? 0;
            const oppName = solo ? "Solo game" : (isA ? m.player_b_name : m.player_a_name) || "Player";
            const youWin = m.winner === user?.user_id;
            const isTie = !solo && !m.winner;
            const myDelta = isA ? m.rating_delta_a : m.rating_delta_b;
            const label = solo ? "Solo" : youWin ? "Win" : isTie ? "Tie" : "Loss";
            const tone = youWin ? "text-nk-accent" : (solo || isTie) ? "text-nk-mute" : "text-nk-bad";
            return (
              <div key={m.match_id} className="nk-card px-4 py-3 flex items-center gap-3" data-testid="duel-result-row">
                <p className="flex-1 min-w-0 text-sm truncate">
                  <span className="font-medium">You</span>
                  <span className="text-nk-mute"> vs </span>
                  <span className="font-medium">{oppName}</span>
                </p>
                <span className="font-mono text-sm shrink-0">{solo ? `${myScore}/5` : `${myScore}–${oppScore}`}</span>
                <span className={`text-xs font-medium w-8 text-right shrink-0 ${tone}`}>{label}</span>
                {!solo && typeof myDelta === "number" && myDelta !== 0 && (
                  <span className={`font-mono text-[11px] shrink-0 ${myDelta >= 0 ? "text-nk-accent" : "text-nk-bad"}`}>
                    {myDelta >= 0 ? "+" : ""}{myDelta}
                  </span>
                )}
              </div>
            );
          };

          return (
            <>
              {pending.length > 0 && (
                <div className="mb-6">
                  <p className="eyebrow mb-3">Needs your move</p>
                  <div className="grid sm:grid-cols-2 gap-3" data-testid="duel-pending-matches">
                    {pending.map(renderPending)}
                  </div>
                </div>
              )}
              <div className="flex items-baseline justify-between mb-3">
                <p className="eyebrow">Recent results</p>
                <Link to="/history?mode=duel" data-testid="duel-history-link"
                      className="text-[11px] text-nk-accent inline-flex items-center gap-1 hover:gap-1.5 transition-all">
                  <ClockCounterClockwise size={11} /> Full history <ArrowRight size={10} />
                </Link>
              </div>
              <div className="space-y-2" data-testid="duel-recent-matches">
                {pending.length === 0 && recent.length === 0 && (
                  <div className="nk-card p-8 text-center text-nk-mute text-sm">
                    No matches yet. Press Find a match to start.
                  </div>
                )}
                {recent.map(renderResult)}
              </div>
            </>
          );
        })()}
      </section>

      <HowToPlay
        title="How NK Duel works"
        steps={[
          { title: "Find a match", desc: "Press Find a match and we pair you with another player. If no one joins in 60 seconds, you play alone." },
          { title: "Answer 5 questions", desc: "You have 15 seconds for each question." },
          { title: "Highest score wins", desc: "If the scores are equal, the faster player wins. Your rating goes up or down after every match." },
        ]}
        points={[
          { label: "Win", value: "+20 SS" },
          { label: "Loss", value: "+5 SS" },
          { label: "Tie", value: "+12 SS each" },
          { label: "Solo game", value: "+5 SS per correct" },
        ]}
      />
    </div>
  );
}
